import GettingStartedProgress from "./GettingStartedProgress";
import * as React from "react";
import { Permission } from "client/resources/permission";
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import {gettingStartedLoader, GettingStartedStatus} from "components/GettingStarted/gettingStartedLoader";
import styles = require("./style.less");
import { GettingStartedDialog } from "components/GettingStarted/GettingStartedDialog";
import FlatButton from "material-ui/FlatButton";
import { isAllowed } from "components/PermissionCheck/PermissionCheck";
import { withRouter, RouteComponentProps } from "react-router";
import getWhatsNext, {WhatsNext} from "components/GettingStarted/WhatsNext/WhatsNext";
import routeLinks from "../../routeLinks";
import {resolveStringPathWithSpaceId} from "../Navigation/resolvePathWithSpaceId";

const imageCongrats = require("./success-ani.svg");

const imageOctopus = require("./Octopus.svg");
const imageEnvironments = require("./Environments.svg");
const imagePackage = require("./Package.svg");
const imageProject = require("./Project.svg");
const imageDeploy = require("./Deploy.svg");

interface ProgressSection {
    percent: number;
    enabled: boolean;
    image: string;
    header: string;
}

interface GettingStartedFooterState {
    showBar: boolean;
    expand: boolean;
    canDismiss: boolean;
    progressSections: ProgressSection[];
    showDetails: boolean;
    status: GettingStartedStatus;
    highlightSection: string;
    spacer: number;
    isDashboard: boolean;
}

type GettingStartedFooterProps = RouteComponentProps<{spaceId: string}>;

class GettingStartedFooter extends BaseComponent<GettingStartedFooterProps, GettingStartedFooterState> {
    unsubscribe: () => void;
    divEl: HTMLElement;

    constructor(props: GettingStartedFooterProps) {
        super(props);

        this.state = {
            showBar: true,
            expand: true,
            canDismiss: false,
            progressSections: null,
            showDetails: false,
            status: null,
            highlightSection: null,
            spacer: 0,
            isDashboard: this.isDashboard(this.props.location.pathname, this.props.match.params.spaceId)
        };
    }

    async componentDidMount() {
        if (!isAllowed({ permission: Permission.AdministerSystem })) {
            this.setState({
                showBar: false
            });
            return;
        }

        const tasksStatus = await gettingStartedLoader.loadStatus();

        if (!this.unmounted) {
            if (tasksStatus.tasks.Deploy.DeployedRelease.IsComplete) {
                this.setState({
                    showBar: false
                });
                return;
            }

            this.setState({
                expand: this.isBarExpanded()
            });

            if (this.divEl) {
                this.setState({
                    spacer: this.divEl.getBoundingClientRect().height
                });
            }

            this.unsubscribe = gettingStartedLoader.subscribe(status => this.refresh(status));
            this.refresh(tasksStatus);
        }
    }

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    componentWillReceiveProps(nextProps: GettingStartedFooterProps) {
        this.setState({
            isDashboard: this.isDashboard(nextProps.location.pathname, nextProps.match.params.spaceId)
        });
    }

    componentDidUpdate() {
        if (!this.divEl) {
            return;
        }

        const newHeight = this.divEl.getBoundingClientRect().height;
        if (this.state.spacer === newHeight) {
            return;
        }

        this.setState({
            spacer: newHeight
        });
    }

    render() {
        if (this.state.isDashboard || !this.state.showBar || !this.state.progressSections) {
            return null;
        }

        const isInEnvironmentPage = this.isInEnvironmentPage(this.props.location.pathname, this.props.match.params.spaceId);

        return <div>
            {this.state.showDetails && <GettingStartedDialog onNavigating={() => this.setState({ showDetails: false })}
                highlightSection={this.state.highlightSection}
                open={this.state.showDetails} onClose={() => this.setState({ showDetails: false })} />}
            <div ref={this.setDivRef} className={styles.gettingStartedFooter}>
                {!this.state.canDismiss && <div className={styles.gettingStartedFooterActions}>
                    <a className={styles.gettingStartedButton} onClick={() => this.toggle()}>
                        <span>Hide</span>
                        <em className="fa fa-minus" aria-hidden="true" />
                    </a>
                    <a className={styles.gettingStartedButton} onClick={() => this.toggleDetail()}>
                        <span>Show detail</span>
                        <em className="fa fa-window-maximize" aria-hidden="true" />
                    </a>
                </div>}
                {!(this.state.canDismiss || this.state.expand) && <div className={styles.gettingStartedMinimize}>
                    <div className={styles.gettingStartedFooterActions}>
                        <a className={styles.gettingStartedButton} onClick={() => this.toggle()}>
                            <span>Show getting started guide</span>
                            <em className="fa fa-plus" aria-hidden="true" />
                        </a>
                    </div>
                </div>}
                {this.state.canDismiss && <div className={styles.gettingStartedMinimize}>
                    <div className={styles.gettingStartedFooterActions}>
                        <a className={styles.gettingStartedButton} onClick={() => this.dismiss()}>
                            <span>Thanks for your help! I don't need the progress bar anymore.</span>
                            <em className="fa fa-times" aria-hidden="true" />
                        </a>
                    </div>
                </div>}
                {this.state.canDismiss && <div className={styles.firstSuccessfulDeploymentContainer}>
                    <div className={styles.firstSuccessfulDeployment}>
                        <div className={styles.successAnimation}>
                            <img src={imageCongrats} alt="Congratulations animation" />
                        </div>
                        <div>
                            <h3><strong>Congratulations!</strong></h3>
                            <p>That was your first successful deployment for this project. We hope everything went smoothly!<br /> Celebrate
                                this moment by tweeting about it.</p>
                            <p><strong>Happy deployments!</strong><br />- The team at Octopus</p>
                            <div>
                                <FlatButton
                                    href="https://g.octopushq.com/OctopusTweetNow?text=We just completed our first successful deployment with @OctopusDeploy!"
                                    target="_blank"
                                    label="Tweet now"
                                    labelStyle={{ color: "#2f93e0" }}
                                    icon={<em style={{ color: "#2f93e0" }} className="fa fa-twitter" aria-hidden="true" />}
                                />
                            </div>
                        </div>
                    </div>
                </div>}
                {!this.state.canDismiss && this.state.expand && <div className={styles.gettingStartedContent}>
                    <div className={styles.whatsNextContainer}>
                        {this.renderWhatsNext(isInEnvironmentPage)}
                    </div>
                    <div className={styles.progressStepWrapper} >
                        {this.state.progressSections.map(s => <span key={s.header} className={styles.progressStep}>
                            <a className={styles.gettingStartedButton}
                                onClick={() => this.toggleDetail(s.header)}>
                                <GettingStartedProgress percent={s.percent} enabled={s.enabled} imgSrc={s.image} header={s.header} />
                            </a>
                        </span>
                        )}
                    </div>
                </div>}
            </div>
            <div style={{ paddingTop: `${this.state.spacer}px` }} />
        </div>;
    }

    private setDivRef = (el: HTMLElement) => {
        this.divEl = el;
    }

    private renderWhatsNext(isInEnvironmentPage: boolean) {
        const whatsNext = getWhatsNext(isInEnvironmentPage,
            this.state.status.next, this.state.status.projects, this.state.status.releases);

        return <div className={styles.whatsNext}>
            <div className={styles.whatsNextText}>{whatsNext.text}</div>
            {whatsNext.element}
        </div>;
    }

    private dismiss() {
        this.setState({ showBar: false });
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }

    private isDashboard(path: string, spaceId: string) {
        return resolveStringPathWithSpaceId(routeLinks.root, spaceId) === path ||
            resolveStringPathWithSpaceId(routeLinks.dashboard.root, spaceId) === path;
    }

    private isInEnvironmentPage(path: string, spaceId: string) {
        return resolveStringPathWithSpaceId(routeLinks.infrastructure.environments.root, spaceId) === path;
    }

    private toggleDetail(highlightSection?: string) {
        this.setState({ showDetails: true, highlightSection });
    }

    private toggle() {
        localStorage.setItem("gettingStartedProgressBarExpanded", (!this.state.expand).toString());
        this.setState({
            expand: !this.state.expand
        });
    }

    private isBarExpanded() {
        if (localStorage.getItem("gettingStartedProgressBarExpanded") !== null) {
            return localStorage.getItem("gettingStartedProgressBarExpanded") === "true" ? true : false;
        }

        return true;
    }

    private refresh(status: GettingStartedStatus) {
        const sections: ProgressSection[] = [{
            percent: 100,
            enabled: true,
            image: imageOctopus,
            header: "OCTOPUS SERVER"
        },
        {
            percent: status.tasks.Infrastructure.Percent,
            enabled: status.tasks.Infrastructure.IsEnabled,
            image: imageEnvironments,
            header: "INFRASTRUCTURE"
        },
        {
            percent: status.tasks.Package.Percent,
            enabled: status.tasks.Package.IsEnabled,
            image: imagePackage,
            header: "PACKAGING"
        },
        {
            percent: status.tasks.Project.Percent,
            enabled: status.tasks.Project.IsEnabled,
            image: imageProject,
            header: "PROJECT"
        },
        {
            percent: status.tasks.Deploy.Percent,
            enabled: status.tasks.Deploy.IsEnabled,
            image: imageDeploy,
            header: "DEPLOY"
        }];

        if (status.tasks.Deploy.DeployedRelease.IsComplete) {
            this.setState({
                canDismiss: true
            });
        }

        this.setState({
            status,
            progressSections: sections
        });
    }
}

export default withRouter<GettingStartedFooterProps>(GettingStartedFooter);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/GettingStarted/GettingStartedFooter.tsx